<template>
  <v-app>
    <v-app-bar app color="#b71c1c" dark dense>
      <v-avatar size="32" class="mr-2">
        <img src="/img/icons/android-chrome-512x512.png" alt="Logo" />
      </v-avatar>
      <span class="white--text text-subtitle-1 font-weight-medium">
        Noormarkun VPK — Hälytysosaston harjoitukset
      </span>
    </v-app-bar>

    <v-main class="grey lighten-5">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="12" lg="12">
            <v-card v-if="loading">
              <v-card-text class="text-center">
                <v-progress-circular
                  indeterminate
                  color="primary"
                ></v-progress-circular>
                <div class="mt-2">Ladataan harjoituksia...</div>
              </v-card-text>
            </v-card>

            <v-card v-else-if="error">
              <v-card-title class="red--text">Virhe</v-card-title>
              <v-card-text>{{ error }}</v-card-text>
            </v-card>

            <template v-else>
              <v-row class="mb-5">
                <v-col cols="12" md="6" v-if="nextHarjoitus">
                  <v-card>
                    <v-list-item>
                      <v-list-item-content>
                        <div class="text-overline mb-4">Harjoitukset</div>
                        <v-list-item-subtitle
                          :class="$vuetify.theme.dark ? 'white--text' : 'black--text'"
                          class="text-h6 mb-1 text-wrap"
                          >{{ nextHarjoitus.subject }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle class="text-wrap"
                          ><span
                            >{{
                              $formatWeekDayDateAndHourMinute(
                                nextHarjoitus.date,
                              )
                            }}
                            {{ formatType(nextHarjoitus.type) }},
                            {{ nextHarjoitus.location }},
                            {{ nextHarjoitus.info }}
                          </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-avatar>
                        <v-icon>mdi mdi-fireplace</v-icon>
                      </v-list-item-avatar>
                    </v-list-item>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6" v-if="nextEvyHarjoitus">
                  <v-card>
                    <v-list-item>
                      <v-list-item-content>
                        <div class="text-overline mb-4">Evy</div>
                        <v-list-item-subtitle
                          :class="$vuetify.theme.dark ? 'white--text' : 'black--text'"
                          class="text-h6 mb-1 text-wrap"
                          >{{ nextEvyHarjoitus.subject }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle class="text-wrap"
                          ><span
                            >{{
                              $formatWeekDayDateAndHourMinute(
                                nextEvyHarjoitus.date,
                              )
                            }}
                            {{ formatType(nextEvyHarjoitus.type) }},
                            {{ nextEvyHarjoitus.location }},
                            {{ nextEvyHarjoitus.info }}
                          </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-avatar>
                        <v-icon>mdi mdi-hospital-box</v-icon>
                      </v-list-item-avatar>
                    </v-list-item>
                  </v-card>
                </v-col>
              </v-row>

              <v-row class="mt-2 mb-5">
                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title>Harjoitukset</v-card-title>
                    <harjoitukset-table
                      :harjoitukset="harjoitukset"
                      :next-harjoitus="nextHarjoitus"
                    />
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card>
                    <v-card-title>Evy harjoitukset</v-card-title>
                    <harjoitukset-table
                      :harjoitukset="evyharjoitukset"
                      :next-harjoitus="nextEvyHarjoitus"
                      type="evy"
                    />
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import dayjs from "dayjs";
import HarjoituksetTable from "@/components/HarjoituksetTable.vue";

const FUNCTIONS_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5001/appnoormarkunvpkfi/europe-west1"
    : "https://europe-west1-appnoormarkunvpkfi.cloudfunctions.net";

export default {
  name: "PublicHarjoitukset",
  components: { HarjoituksetTable },
  data() {
    return {
      loading: true,
      error: null,
      harjoitukset: [],
      evyharjoitukset: [],
      refreshTimer: null,
    };
  },
  computed: {
    nextHarjoitus() {
      const today = dayjs();
      return (
        this.harjoitukset.find((h) => dayjs(h.date).isAfter(today)) || null
      );
    },
    nextEvyHarjoitus() {
      const today = dayjs();
      return (
        this.evyharjoitukset.find((h) => dayjs(h.date).isAfter(today)) || null
      );
    },
  },
  methods: {
    formatType(type) {
      return type === "OT" ? "Oppitunti" : "";
    },
    async loadData() {
      const token = this.$route.params.token;
      try {
        const response = await fetch(
          `${FUNCTIONS_BASE}/getPublicHarjoitukset?token=${encodeURIComponent(
            token,
          )}`,
        );
        if (response.status === 404) {
          this.error = "Virheellinen tai vanhentunut linkki.";
          return;
        }
        if (!response.ok) {
          this.error = "Tietojen lataaminen epäonnistui.";
          return;
        }
        const data = await response.json();
        this.harjoitukset = data.harjoitukset || [];
        this.evyharjoitukset = data.evy || [];
        this.error = null;
      } catch (e) {
        this.error = "Yhteysvirhe. Tarkista verkkoyhteytesi.";
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadData();
    this.refreshTimer = setTimeout(() => {
      window.location.reload();
    }, 24 * 60 * 60 * 1000);
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
  },
};
</script>

<style></style>
